import { Logic } from '~common/ui-logic/logic'
import { executeTask, TaskState } from '~common/ui-logic/tasks'
import type { SettingsModalDeps, SettingsModalState } from './types'
import { DateFormat } from '~common/utils/date-transformation/types'
import { formatISODate } from '~common/utils/date-transformation/date-transformations'
import type { AppContextType } from '~/app-context'
import { httpsCallable } from 'firebase/functions'
import { sleep } from '~common/utils/sleep'
import { i18n } from '~/i18n/config'
import { DEFAULT_SKR03_CATEGORY_IDS } from '../transactions/services/skr-03-categories'
import { DEFAULT_SKR04_CATEGORY_IDS } from '../transactions/services/skr-04-categories'
import {
    getCategoriesFiltered,
    SKRCategory,
    getAllCategories,
} from '../transactions/services/categories'
import {
    getDatevPluginConfig,
    getAllPluginConfigs,
    PLUGIN_CONFIGS,
    type PluginId,
    getPluginConfig,
} from '../plugins/config'
import type { DatevPluginSettings, GetMyInvoicesPluginSettings } from './types'
import { GetMyInvoicesPluginConfig } from '~common/storage/users'

export class SettingsModalLogic extends Logic<
    SettingsModalDeps & AppContextType,
    SettingsModalState
> {
    getInitialState = (): SettingsModalState => {
        const allPlugins = getAllPluginConfigs()
        const expandedPlugins: Record<PluginId, boolean> = {} as Record<
            PluginId,
            boolean
        >
        for (const plugin of allPlugins) {
            expandedPlugins[plugin.id] = false
        }

        return {
            companyName: '',
            addressLine: '',
            countryCode: '',
            dateFormat: DEFAULT_DATE_FORMAT,
            isSaving: false,
            error: null,

            // Delete account
            showDeleteConfirm: false,
            deleteConfirmationText: '',
            isDeleting: false,
            deleteError: null,
            loadState: 'pristine',
            telegramExpanded: false,
            dangerZoneExpanded: false,
            companyInfoExpanded: false,
            dateFormatExpanded: false,
            emailForwardingExpanded: false,
            pluginSettingsExpanded: false,
            languageExpanded: false,
            language: 'en',
            messengerVerificationCode: '',

            // Plugin settings
            expandedPlugins,
            pluginSettings: {},
        }
    }

    async initialize() {
        await executeTask(this, 'loadState', async () => {
            await this.loadUserData()
        })
        this.deps.services.cache.onUpdate.listen((updatedKeys) => {
            if (updatedKeys.has('settings')) {
                this.loadUserData()
            }
            if (updatedKeys.has('authState')) {
                this.loadUserData()
            }
        })

        // Compute full sorted categories after loading data (one-time on init, using current actives)
        const datevSettings = this.getDatevSettings()
        if (datevSettings?.kontenrahmen) {
            this.computeFullSortedCategories()
        }

        if (this.deps.openSection === 'booking-categories') {
            this.setState({
                pluginSettingsExpanded: true,
                expandedPlugins: {
                    ...this.state.expandedPlugins,
                    'datev-classify-transactions': true,
                },
            })
        } else if (this.deps.openSection === 'plugin-settings') {
            // General plugin settings section request - expand section
            this.setState({
                pluginSettingsExpanded: true,
            })
        } else if (this.deps.openSection === 'company-info') {
            this.setState({
                companyInfoExpanded: true,
            })
        } else if (this.deps.openSection === 'date-format') {
            this.setState({
                dateFormatExpanded: true,
            })
        } else if (this.deps.openSection === 'email-forwarding') {
            this.setState({
                emailForwardingExpanded: true,
            })
        } else if (this.deps.openSection === 'telegram') {
            this.setState({
                telegramExpanded: true,
            })
        } else if (this.deps.openSection === 'danger-zone') {
            this.setState({
                dangerZoneExpanded: true,
            })
        } else if (this.deps.openSection === 'language') {
            this.setState({
                languageExpanded: true,
            })
        }
    }

    private async loadUserData() {
        if (!this.deps.storageModules) return
        const settings = await this.deps.services.cache.getSettings()
        const datevPluginConfig = getDatevPluginConfig(settings)
        const pluginConfig = datevPluginConfig?.config

        const datevSettings: DatevPluginSettings = {
            kontenrahmen: pluginConfig?.kontenrahmen,
            kleinunternehmer: pluginConfig?.kleinunternehmer || false,
            customInstructions: pluginConfig?.prompt || '',
            activeCategories: pluginConfig?.activeCategories || [],
            categorySearchQuery:
                this.getDatevSettings()?.categorySearchQuery || '',
        }

        // NEW: Set defaults if kontenrahmen set but no actives (for first load)
        if (
            datevSettings.kontenrahmen === 'SKR03' &&
            datevSettings.activeCategories.length === 0
        ) {
            datevSettings.activeCategories = DEFAULT_SKR03_CATEGORY_IDS
        } else if (
            datevSettings.kontenrahmen === 'SKR04' &&
            datevSettings.activeCategories.length === 0
        ) {
            datevSettings.activeCategories = DEFAULT_SKR04_CATEGORY_IDS
        }

        // Load GetMyInvoices config
        const gmiPluginConfig = getPluginConfig('get-my-invoices', settings)
        // Check if apiKey exists on the plugin config object
        // Cast to any because TS might not infer the union type properties correctly in all contexts without guards
        const gmiApiKey =
            (gmiPluginConfig as GetMyInvoicesPluginConfig)?.apiKey || ''

        const currentLanguage = i18n.language || 'en'
        this.setState({
            ...this.state,
            companyName: settings?.companyInfo?.companyName || '',
            addressLine: settings?.companyInfo?.addressLine || '',
            countryCode: settings?.companyInfo?.countryCode || '',
            dateFormat: settings?.dateFormat || DEFAULT_DATE_FORMAT,
            language: currentLanguage,
            pluginSettings: {
                ...this.state.pluginSettings,
                'datev-classify-transactions': datevSettings,
                'get-my-invoices': {
                    apiKey: gmiApiKey ? '********' : '', // Placeholder if set, empty otherwise
                },
            },
        })

        // DEBUG: Log loaded settings
        console.warn('Loaded datevSettings:', {
            kontenrahmen: datevSettings.kontenrahmen,
            activeCategoriesLength: datevSettings.activeCategories.length,
        })

        const authData = this.deps.services.cache.getKey('authState').data
        const messengerVerificationCode =
            authData?.messengerVerificationCode || ''
        this.setState({
            ...this.state,
            messengerVerificationCode,
        })
    }

    updateCompanyName = (companyName: string) => {
        this.setState({ ...this.state, companyName, error: null })
    }

    updateAddressLine = (addressLine: string) => {
        this.setState({ ...this.state, addressLine, error: null })
    }

    updateCountryCode = (countryCode: string) => {
        this.setState({ ...this.state, countryCode, error: null })
    }

    updateDateFormat = (dateFormat: DateFormat) => {
        this.setState({ ...this.state, dateFormat, error: null })
    }

    getDateFormatOptions = () => DATE_FORMAT_OPTIONS

    /**
     * Format an invoice date (ISO string YYYY-MM-DD) to user's preferred format
     * This is the main method for converting invoice dates for display
     */
    formatInvoiceDate = (isoDate: string): string => {
        return formatISODate(isoDate, this.state.dateFormat)
    }

    /**
     * Get current date in user's preferred format
     */
    getCurrentDateFormatted = (): string => {
        const now = new Date()
        const isoDate = now.toISOString().split('T')[0] // YYYY-MM-DD format
        return formatISODate(isoDate, this.state.dateFormat)
    }

    async saveSettings() {
        if (!this.deps.storageModules) return

        this.setState({ ...this.state, isSaving: true, error: null })

        try {
            const user = await this.deps.storageModules.auth.getCurrentUser()
            if (!user) {
                throw new Error('User not found')
            }

            const currentSettings = await this.deps.services.cache.getSettings()
            const datevPluginConfig = getDatevPluginConfig(currentSettings)
            const datevSettings = this.getDatevSettings()
            const gmiSettings = this.getGetMyInvoicesSettings()

            const updatedPlugins = {
                ...currentSettings?.plugins,
            }

            if (datevSettings) {
                const config: Record<string, unknown> = {}
                if (datevSettings.kontenrahmen !== undefined) {
                    config.kontenrahmen = datevSettings.kontenrahmen
                }
                config.kleinunternehmer = datevSettings.kleinunternehmer
                config.activeCategories = datevSettings.activeCategories
                if (datevSettings.customInstructions !== undefined) {
                    config.prompt = datevSettings.customInstructions
                }

                updatedPlugins['datev-classify-transactions'] = {
                    enabled: datevPluginConfig?.enabled ?? false,
                    config,
                }
            }

            if (gmiSettings) {
                const currentGmiConfig = getPluginConfig(
                    'get-my-invoices',
                    currentSettings,
                )
                const enabled = currentGmiConfig?.enabled ?? false
                let apiKeyToSave = (
                    currentGmiConfig as GetMyInvoicesPluginConfig
                )?.apiKey

                // If user entered a new key (not the placeholder), encrypt and save it
                if (
                    gmiSettings.apiKey &&
                    gmiSettings.apiKey !== '********' &&
                    gmiSettings.apiKey.trim() !== ''
                ) {
                    const response = (await httpsCallable(
                        this.deps.services._functions,
                        'encryptString',
                    )({ text: gmiSettings.apiKey })) as {
                        data: { encrypted: string }
                    }
                    apiKeyToSave = response.data.encrypted
                } else if (gmiSettings.apiKey === '') {
                    // If cleared, remove it
                    apiKeyToSave = ''
                }

                updatedPlugins['get-my-invoices'] = {
                    enabled,
                    apiKey: apiKeyToSave,
                }
            }

            await this.deps.storageModules.auth.updateUser(user.id, {
                settings: {
                    dateFormat: this.state.dateFormat,
                    companyInfo: {
                        companyName: this.state.companyName,
                        addressLine: this.state.addressLine,
                        countryCode: this.state.countryCode,
                    },
                    plugins: updatedPlugins,
                },
            })

            this.deps.services?.cache.updateSettings({
                dateFormat: this.state.dateFormat,
                companyInfo: {
                    companyName: this.state.companyName,
                    addressLine: this.state.addressLine,
                    countryCode: this.state.countryCode,
                },
                plugins: updatedPlugins,
            })

            const currentAuthData =
                this.deps.services?.cache.getKey('authState').data
            this.deps.services?.cache.updateAuthState({
                data: currentAuthData ?? null,
            })

            this.deps.onClose()
            this.setState({ isSaving: false })
        } catch (error) {
            this.setState({
                ...this.state,
                isSaving: false,
                error:
                    error instanceof Error
                        ? error.message
                        : 'Failed to save settings',
            })
        }
    }

    canSave = (): boolean => {
        return (
            this.state.companyName.trim() !== '' &&
            this.state.addressLine.trim() !== '' &&
            this.state.countryCode.trim() !== ''
        )
    }

    toggleDeleteConfirm = () => {
        this.setState({
            ...this.state,
            showDeleteConfirm: !this.state.showDeleteConfirm,
            deleteError: null,
        })
    }

    updateDeleteConfirmationText = (text: string) => {
        this.setState({
            ...this.state,
            deleteConfirmationText: text,
            deleteError: null,
        })
    }

    async deleteAccount() {
        if (this.state.deleteConfirmationText.trim() !== 'Delete') {
            this.setState({
                ...this.state,
                deleteError:
                    'Please type "Delete" to confirm account deletion.',
            })
            return
        }

        this.setState({
            ...this.state,
            isDeleting: true,
            deleteError: null,
        })

        try {
            // Delete server-side data first (requires valid auth token for verification)
            await httpsCallable(
                this.deps.services._functions,
                'deleteUserAccount',
            )()

            // Clear localStorage (importer/queue states, etc.)
            localStorage.clear()

            // Clear IndexedDB databases (Firestore persistence + custom caches)
            const clearDB = (name: string): Promise<void> =>
                new Promise((resolve, reject) => {
                    const req = indexedDB.deleteDatabase(name)
                    req.onerror = () => reject(req.error)
                    req.onsuccess = () => resolve()
                })
            clearDB('firebaseLocalStorageDb')
            clearDB('firestore/(DEFAULT)/taxing-desktop/main')
            clearDB('taxing-local')
            clearDB('firebase-heartbeat-database')
            await sleep(6000)
            // Reload the page for clean reinitialization
            window.location.reload()
        } catch (error) {
            this.setState({
                ...this.state,
                isDeleting: false,
                deleteError:
                    error instanceof Error
                        ? error.message
                        : 'Failed to delete account.',
            })
        }
    }

    toggleTelegramExpanded = () => {
        this.setState({ telegramExpanded: !this.state.telegramExpanded })
    }

    toggleDangerExpanded = () => {
        this.setState({ dangerZoneExpanded: !this.state.dangerZoneExpanded })
    }

    toggleCompanyInfoExpanded = () => {
        this.setState({ companyInfoExpanded: !this.state.companyInfoExpanded })
    }

    toggleDateFormatExpanded = () => {
        this.setState({ dateFormatExpanded: !this.state.dateFormatExpanded })
    }

    toggleEmailForwardingExpanded = () => {
        this.setState({
            emailForwardingExpanded: !this.state.emailForwardingExpanded,
        })
    }

    toggleLanguageExpanded = () => {
        this.setState({ languageExpanded: !this.state.languageExpanded })
    }

    updateLanguage = (language: string) => {
        this.setState({ ...this.state, language, error: null })
        i18n.changeLanguage(language)
    }

    getTelegramBotLink = (): string => {
        if (!this.state.messengerVerificationCode) return ''
        const nodeEnv =
            this.deps.services.config.getConfig('environment').nodeEnv
        const botUsername =
            nodeEnv === 'development' ? 'taxing_app_dev_bot' : 'taxing_app_bot'
        return `https://t.me/${botUsername}?start=${this.state.messengerVerificationCode}`
    }

    getEmailForwardingAddress = (): string => {
        return `incoming+${this.state.messengerVerificationCode}@mail.taxing.app`
    }

    togglePluginSettingsExpanded = () => {
        this.setState({
            pluginSettingsExpanded: !this.state.pluginSettingsExpanded,
        })
    }

    togglePluginExpanded = (pluginId: PluginId) => {
        this.setState({
            expandedPlugins: {
                ...this.state.expandedPlugins,
                [pluginId]: !this.state.expandedPlugins[pluginId],
            },
        })
    }

    getEnabledPlugins = (): PluginId[] => {
        const settings = this.deps.services.cache.getKey('settings')
        if (!settings?.plugins) return []

        return getAllPluginConfigs()
            .filter((plugin) => {
                const pluginConfig = settings.plugins?.[plugin.id]
                return pluginConfig?.enabled === true
            })
            .map((plugin) => plugin.id) as PluginId[]
    }

    getPluginsToDisplay = (): PluginId[] => {
        if (this.deps.openSection === 'booking-categories') {
            return ['datev-classify-transactions']
        }

        return this.getEnabledPlugins()
    }

    isShowingSpecificPlugin = (): boolean => {
        return this.deps.openSection === 'booking-categories'
    }

    getPluginTitle = (pluginId: PluginId): string => {
        return PLUGIN_CONFIGS[pluginId]?.title || pluginId
    }

    getPluginDescription = (pluginId: PluginId): string => {
        return PLUGIN_CONFIGS[pluginId]?.description || ''
    }

    getDatevSettings = (): DatevPluginSettings | undefined => {
        return this.state.pluginSettings['datev-classify-transactions'] as
            | DatevPluginSettings
            | undefined
    }

    getGetMyInvoicesSettings = (): GetMyInvoicesPluginSettings | undefined => {
        return this.state.pluginSettings['get-my-invoices'] as
            | GetMyInvoicesPluginSettings
            | undefined
    }

    updateGetMyInvoicesApiKey = (apiKey: string) => {
        const current = this.getGetMyInvoicesSettings() || { apiKey: '' }
        this.setState({
            pluginSettings: {
                ...this.state.pluginSettings,
                'get-my-invoices': {
                    ...current,
                    apiKey,
                },
            },
        })
    }

    updateDatevSettings = (updates: Partial<DatevPluginSettings>) => {
        const current = this.getDatevSettings() || {
            kontenrahmen: undefined,
            kleinunternehmer: false,
            customInstructions: '',
            activeCategories: [],
            categorySearchQuery: '',
        }
        this.setState({
            pluginSettings: {
                ...this.state.pluginSettings,
                'datev-classify-transactions': {
                    ...current,
                    ...updates,
                },
            },
        })
    }

    updateKontenrahmen = (kontenrahmen: 'SKR03' | 'SKR04' | undefined) => {
        const updates: Partial<DatevPluginSettings> = { kontenrahmen }
        if (kontenrahmen === 'SKR03') {
            updates.activeCategories = DEFAULT_SKR03_CATEGORY_IDS
        } else if (kontenrahmen === 'SKR04') {
            updates.activeCategories = DEFAULT_SKR04_CATEGORY_IDS
        }
        this.updateDatevSettings(updates)

        // Re-compute full sorted list when kontenrahmen changes (using new current actives)
        this.computeFullSortedCategories()
    }

    updateKleinunternehmer = (kleinunternehmer: boolean) => {
        this.updateDatevSettings({ kleinunternehmer })
    }

    updateCustomInstructions = (customInstructions: string) => {
        this.updateDatevSettings({ customInstructions })
    }

    updateCategorySearchQuery = (categorySearchQuery: string) => {
        this.updateDatevSettings({ categorySearchQuery })
    }

    toggleCategoryActive = (categoryId: number) => {
        const datevSettings = this.getDatevSettings()
        if (!datevSettings) return

        const current = datevSettings.activeCategories
        const index = current.indexOf(categoryId)
        const newCategories =
            index >= 0
                ? current.filter((id) => id !== categoryId)
                : [...current, categoryId]

        this.updateDatevSettings({ activeCategories: newCategories })

        // Removed: No re-sort on toggle - keep fixed order from load
    }

    // Updated: Uses initial activeCategories for fixed sorting order (no dynamic re-sort)
    private computeFullSortedCategories() {
        const datevSettings = this.getDatevSettings()
        if (!datevSettings?.kontenrahmen) return

        // DEBUG: Log before sort
        console.warn(
            'Computing sort: kontenrahmen=',
            datevSettings.kontenrahmen,
            'actives count=',
            datevSettings.activeCategories.length,
        )

        const allCategories = getAllCategories({
            kontenrahmen: datevSettings.kontenrahmen,
        }) // Full unfiltered
        const initialActiveSet = new Set(datevSettings.activeCategories)
        const fullSorted = Object.values(allCategories).sort((a, b) => {
            const isActiveA = initialActiveSet.has(Number(a.id))
            const isActiveB = initialActiveSet.has(Number(b.id))
            if (isActiveA !== isActiveB) {
                return isActiveA ? -1 : 1
            }
            return parseInt(a.id) - parseInt(b.id)
        })

        // DEBUG: Log first few after sort (expand in console to see isActive)
        console.warn(
            'First 5 sorted (id, label, isActive):',
            fullSorted.slice(0, 5).map((c) => ({
                id: c.id,
                label: c.label,
                isActive: initialActiveSet.has(Number(c.id)),
            })),
        )

        // Store in state (immutable update)
        this.updateDatevSettings({ fullSortedCategories: fullSorted })
    }

    getAvailableCategories = (): SKRCategory[] => {
        const datevSettings = this.getDatevSettings()
        if (!datevSettings?.kontenrahmen) return []

        const searchQuery = datevSettings.categorySearchQuery
            .toLowerCase()
            .trim()
        const fullSorted = datevSettings.fullSortedCategories || []

        // Filter the pre-sorted list by searchQuery (preserves order)
        let filteredList: SKRCategory[] = fullSorted
        if (searchQuery) {
            const tokens = searchQuery
                .split(/\s+/)
                .filter((token) => token.length > 0)
            filteredList = fullSorted.filter((category) => {
                return tokens.every((token) => {
                    if (/^\d+$/.test(token)) {
                        return category.id.includes(token)
                    } else {
                        return (
                            category.label.toLowerCase().includes(token) ||
                            category.description.toLowerCase().includes(token)
                        )
                    }
                })
            })
        }

        // DEBUG: Log available
        console.warn(
            'Available categories: fullSorted length=',
            fullSorted.length,
            'filtered length=',
            filteredList.length,
            'searchQuery=',
            searchQuery,
        )

        // Return ordered array directly (no Record, preserves sort perfectly)
        return filteredList
    }
}

const DEFAULT_DATE_FORMAT: DateFormat = 'MM/DD/YYYY'

const DATE_FORMAT_OPTIONS: Array<{
    value: DateFormat
    label: string
    example: string
}> = [
    {
        value: 'MM/DD/YYYY',
        label: 'US Format (MM/DD/YYYY)',
        example: '12/25/2023',
    },
    {
        value: 'DD/MM/YYYY',
        label: 'European Format (DD/MM/YYYY)',
        example: '25/12/2023',
    },
    {
        value: 'YYYY-MM-DD',
        label: 'ISO Format (YYYY-MM-DD)',
        example: '2023-12-25',
    },
    {
        value: 'DD.MM.YYYY',
        label: 'German Format (DD.MM.YYYY)',
        example: '25.12.2023',
    },
    {
        value: 'MM-DD-YYYY',
        label: 'US with Dashes (MM-DD-YYYY)',
        example: '12-25-2023',
    },
    {
        value: 'DD-MM-YYYY',
        label: 'European with Dashes (DD-MM-YYYY)',
        example: '25-12-2023',
    },
    {
        value: 'YYYY/MM/DD',
        label: 'ISO with Slashes (YYYY/MM/DD)',
        example: '2023/12/25',
    },
    {
        value: 'MMM DD, YYYY',
        label: 'US with Month Name (MMM DD, YYYY)',
        example: 'Dec 25, 2023',
    },
    {
        value: 'DD MMM YYYY',
        label: 'European with Month Name (DD MMM YYYY)',
        example: '25 Dec 2023',
    },
    {
        value: 'YYYY年MM月DD日',
        label: 'Japanese Format (YYYY年MM月DD日)',
        example: '2023年12月25日',
    },
]
